import { evaluate, tokenize } from '@zk-kit/logical-expressions'

// Define the return type for the evaluateCustomFormula function
interface EvaluateResult {
  isValid: boolean
  result?: boolean
}

/**
 * Evaluates a custom formula based on provided condition results.
 *
 * @param formula - A logical formula string that includes condition references (e.g., "1 AND 2").
 * @param conditionResults - An array of boolean values representing the conditions for evaluation.
 * @returns EvaluateResult - The result of the evaluation, including whether the formula is valid and the evaluation result.
 */
export const evaluateCustomFormula = (formula: string, conditionResults: boolean[]): EvaluateResult => {
  const expression = formula.toLowerCase().replace(/(\d+)/g, (match) => {
    const index = parseInt(match) - 1
    return index >= 0 && index < conditionResults.length ? conditionResults[index].toString() : 'false'
  })

  try {
    const tokens = tokenize(expression)
    const result = evaluate(tokens)
    return { result, isValid: true }
  } catch {
    return { isValid: false }
  }
}

/**
 * Validates if a custom formula is syntactically correct based on the number of conditions.
 *
 * @param formula - A logical formula string that includes condition references (e.g., "1 AND 2").
 * @param conditionsLength - The total number of conditions available for evaluation.
 * @returns boolean - Returns true if the formula is valid, false otherwise.
 */
export const isCustomFormulaValid = (formula: string, conditionsLength: number): boolean => {
  const matchResult = formula.match(/\d+/g) ?? []
  const numbers: number[] = matchResult.map(Number)
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0

  if (
    numbers.some(nr => nr === 0) ||
    maxNumber > conditionsLength
  ) return false

  const testArray = new Array(conditionsLength).fill(true)
  const { isValid } = evaluateCustomFormula(formula, testArray)
  return isValid
}
